import React from 'react';
import { ArrowRight, BookOpen, Users, BarChart3 } from 'lucide-react';
import { comparativoGeneralUrl } from '../data';
import { pageTransitionSlow } from '../constants/pageMotion';
import AnimatedPage from './ui/AnimatedPage';
import PageSectionHeader from './ui/PageSectionHeader';
import SectionDivider from './ui/SectionDivider';
import DocumentoDownloadCard from './DocumentoDownloadCard';
import NavFeatureCard from './NavFeatureCard';
import HighlightBulletItem from './ui/HighlightBulletItem';

const NAV_CARDS = [
  {
    tab: 'propuestas',
    icon: BookOpen,
    title: '1. Comparar Propuestas',
    desc: 'Analiza cara a cara las propuestas temáticas de ambos candidatos en temas esenciales como educación, economía, seguridad y salud.',
    cta: 'Comparar propuestas',
    borderColor: 'hover:border-amber-200',
    iconBg: 'bg-amber-50 border-amber-100/50',
    iconColor: 'text-amber-600'
  },
  {
    tab: 'candidatos',
    icon: Users,
    title: '2. Conocer los Perfiles',
    desc: 'Explora a fondo las trayectorias públicas, lemas de campaña, ideologías y fichas biográficas detalladas de cada candidato.',
    cta: 'Conocer perfiles',
    borderColor: 'hover:border-indigo-200',
    iconBg: 'bg-indigo-50 border-indigo-100/50',
    iconColor: 'text-indigo-600'
  },
  {
    tab: 'datos',
    icon: BarChart3,
    title: '3. Ver Análisis Multimedia',
    desc: 'Accede a lecturas de análisis en profundidad, debates en video integrados de YouTube y widgets con señales clave de votación.',
    cta: 'Ver análisis',
    borderColor: 'hover:border-slate-300',
    iconBg: 'bg-slate-100 border-slate-200/50',
    iconColor: 'text-slate-600'
  }
];

export default function Home({ setActiveTab, perfilesCandidatos = {} }) {
  const planCepeda = perfilesCandidatos?.cepeda?.planGobiernoUrl ?? '#';
  const planEspriella = perfilesCandidatos?.deLaEspriella?.planGobiernoUrl ?? '#';

  return (
    <AnimatedPage className="max-w-7xl mx-auto px-4 py-12 md:px-6 lg:py-20" transition={pageTransitionSlow}>
      <PageSectionHeader
        as="h1"
        className="max-w-4xl mx-auto"
        eyebrow="Elecciones Colombia 2026"
        eyebrowClassName="bg-slate-100 text-slate-600 text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest border border-slate-200/40"
        title={
          <>
            Hacia un <br className="sm:hidden" />
            <span className="bg-gradient-to-r from-amber-500 via-blue-600 to-red-600 bg-clip-text text-transparent">
              Voto Consciente 2026
            </span>
          </>
        }
        titleClassName="mt-6 text-4xl font-extrabold leading-tight sm:text-5xl md:text-6xl text-slate-900 tracking-tight"
        flagSize="md"
        flagClassName="my-6"
        description="Compara de manera imparcial y estructurada los programas presidenciales, trayectorias y datos estadísticos para tomar una decisión informada en la segunda vuelta electoral."
        descriptionClassName="mx-auto max-w-2xl text-lg text-slate-600 leading-relaxed md:text-xl"
      >
        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={() => setActiveTab('propuestas')}
            className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-7 py-4 text-sm font-semibold text-white shadow-lg shadow-slate-900/10 transition-all hover:scale-[1.02] hover:bg-slate-800"
          >
            Empezar ahora
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </PageSectionHeader>

      <div className="mt-14 md:mt-20 mb-12">
        <SectionDivider label="Documentos Oficiales" />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3 md:gap-4">
          <DocumentoDownloadCard href={planCepeda} label="Plan de Gobierno — Iván Cepeda" variant="blue" />
          <DocumentoDownloadCard
            href={planEspriella}
            label="Plan de Gobierno — Abelardo de la Espriella"
            variant="red"
          />
          <DocumentoDownloadCard href={comparativoGeneralUrl} label="Comparativa General (PDF)" variant="neutral" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
        {NAV_CARDS.map((card) => (
          <NavFeatureCard
            key={card.tab}
            icon={card.icon}
            title={card.title}
            description={card.desc}
            cta={card.cta}
            borderColor={card.borderColor}
            iconBg={card.iconBg}
            iconColor={card.iconColor}
            onClick={() => setActiveTab(card.tab)}
          />
        ))}
      </div>

      <div className="bg-white border border-slate-100 rounded-3xl p-8 md:p-10 shadow-sm relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-amber-200/50 via-blue-200/50 to-red-200/50" />
        <div className="max-w-3xl">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Visión del Electorado</span>
          <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 mt-3 mb-4">
            Construyendo Democracia en Colombia
          </h2>
          <p className="text-slate-600 leading-relaxed mb-8">
            Este portal ha sido diseñado para ofrecer información libre de sesgos, reuniendo datos de fuentes
            públicas organizados temáticamente. Promovemos un ejercicio democrático responsable fundamentado en el
            análisis crítico e informado de las opciones del electorado.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <HighlightBulletItem dotColor="bg-amber-400">
              Educación, Salud y Economía comparada
            </HighlightBulletItem>
            <HighlightBulletItem dotColor="bg-indigo-400">
              Trayectorias y posturas oficiales en PDF
            </HighlightBulletItem>
          </div>
        </div>
      </div>
    </AnimatedPage>
  );
}
